/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import DataActionBaseComposer, { DataActionBaseComposerArgs } from 'cio/components/action-composer/data-action/base';
import { AttributePreview } from 'cio/utils/previews';
import { action } from '@ember/object';
import DataActionRecipientCondition from 'cio/app-models/data-action/recipient-condition';
import { tracked } from '@glimmer/tracking';
import DataActionBodyAttributeRows from 'cio/app-models/data-action/body-attribute-rows';
import { htmlSafe } from '@ember/template';
import { DataActionConditionType, DATA_ACTION_CONDITION_TYPE_LABEL } from 'cio/utils/campaigns/data-action';

interface CreateOrUpdatePersonComposerArgs extends DataActionBaseComposerArgs {
  preview: AttributePreview;
}

interface CreateOrUpdatePersonComposerSignature {
  Args: CreateOrUpdatePersonComposerArgs;
  Element: HTMLDivElement;
}

export default class CreateOrUpdatePersonComposer extends DataActionBaseComposer<CreateOrUpdatePersonComposerSignature> {
  previewObserverKeys = ['args.editableEntity.template.{body,editor,recipient}', 'bodyRows.isDirty'];
  previewURL = 'previews/create_or_update_person';

  constructor(owner: unknown, args: CreateOrUpdatePersonComposerSignature['Args']) {
    super(owner, args);
    this.deserializeRecipientCondition();
    this.deserializeAttributeUpdates();
  }

  getPreviewData() {
    return {
      body: this.bodyRows.serialize(),
      recipient: this.recipientCondition.serialize(),
    };
  }

  @tracked recipientCondition!: DataActionRecipientCondition;
  @tracked bodyRows!: DataActionBodyAttributeRows;

  private deserializeRecipientCondition() {
    this.recipientCondition = DataActionRecipientCondition.deserialize(this.template.recipient as string, {
      defaultType: this.defaultConditionType,
      mustMatchRecipient: this.args.editableEntity.campaign?.isDataCampaign,
    });
  }

  private deserializeAttributeUpdates() {
    this.bodyRows = DataActionBodyAttributeRows.deserialize(this.template.body, {
      defaultType: this.defaultConditionType,
      minimumRowsCount: 0,
      stringifyEmptyRows: false,
      actionType: 'attribute_update_action',
    });
  }

  get isDirty() {
    return this.template.get('hasDirtyAttributes') || this.recipientCondition.isDirty || this.bodyRows.isDirty;
  }

  get isInvalid() {
    if (!this.recipientCondition.isValid) {
      return true;
    }

    return this.bodyRows.hasDuplicateRowNames || this.bodyRows.hasErrors || this.bodyRows.hasIllegalEmptyValues;
  }

  get showSaveTooltip() {
    return this.saveTooltipContent !== false;
  }

  get saveTooltipContent() {
    if (!this.recipientCondition.isValid) {
      return 'You need to map the identifier to a value to find a person.';
    }

    if (this.bodyRows.hasIllegalEmptyValues) {
      return htmlSafe(
        `Enter a value for all selected attributes, or use the <b>${
          DATA_ACTION_CONDITION_TYPE_LABEL[DataActionConditionType.RemoveAttribute]
        }</b> value type.`,
      );
    }

    return false;
  }

  @action
  async save() {
    if (this.isInvalid) {
      return;
    }

    this.template.recipient = this.recipientCondition.serialize();
    this.template.body = this.bodyRows.serialize();

    await this.args.onSave();
    this.deserializeRecipientCondition();
    this.deserializeAttributeUpdates();
    this.resetDirtyState();
  }

  @action
  rollback() {
    this.template.rollbackAttributes();
    this.deserializeRecipientCondition();
    this.deserializeAttributeUpdates();
    this.resetDirtyState();
    this.args.onRollback();
  }

  private resetDirtyState() {
    this.args.onDirtyChange?.(false);
  }

  @action
  onDirtyChange(_element: HTMLElement, [isDirty]: [boolean]) {
    this.args.onDirtyChange?.(isDirty);
  }
}
